import { baseApiAxios } from '../../../js/portal/api';
import {
  addToast,
  createJsonCodeBlock,
  hasAccess,
  InfoTable,
  InfoTableWide,
  showErrorFromApiOperation,
} from '../../../js/portal/main';
import { addSpinner, removeSpinners } from '../../../js/portal/sidebar';
import { deleteZoneOrder } from '../../../js/portal/zones';
import { DetailsTabLayout, TabPane, DetailSection } from './DetailsTabLayout';

async function hostedZoneUpdateHandler(fqdn) {
  try {
    addSpinner();
    const trigger_update_response = await baseApiAxios.triggerHostedZoneUpdate(fqdn);
    removeSpinners();
    addToast('Hosted Zone Update', trigger_update_response.message, 6000);
  } catch (err) {
    removeSpinners();
    showErrorFromApiOperation('Triggering Hosted Zone Update failed.')(err);
  }
}

async function deleteHostedZoneFromDB(fqdn) {
  try {
    addSpinner();
    await baseApiAxios.deleteHostedZoneFromDB(fqdn);
    removeSpinners();
    addToast('Hosted Zone deletion from DB', `Successfully requested deletion of hosted zone ${fqdn} from DB.`, 6000);
  } catch (err) {
    removeSpinners();
    showErrorFromApiOperation('Hosted Zone deletion from DB failed.')(err);
  }
}

export default function HostedZoneTabs(props) {
  const { data } = props;

  if (!data.account_item) data.account_item = {};
  if (!data.hosted_zone) data.hosted_zone = {};

  const { account_item, hosted_zone } = data;

  const associatedVPCsEnabled = hosted_zone.vpcs && Object.keys(hosted_zone.vpcs).length;
  const nameServersEnabled = hosted_zone.ns_records && Object.keys(hosted_zone.ns_records).length;
  const dnssecEnabled = hosted_zone.dnssec && Object.keys(hosted_zone.dnssec).length;
  const queryLoggingConfigEnabled =
    hosted_zone.query_logging_config && Object.keys(hosted_zone.query_logging_config).length;

  // shared
  const hosted_zone_data = {
    account_id: hosted_zone.account_id,
    fqdn: hosted_zone.fqdn,
    status: hosted_zone.status,
    zone_details: hosted_zone.zone_details,
    zone_id: hosted_zone.zone_id || hosted_zone.hosted_zone_id,
  };

  const hostedZoneButtonHelper = () => {
    return (
      <div class="detailsContent">
        <div class="btn-group detail-actions-btn-group">
          {['AccountDeleted'].indexOf(hosted_zone.status) < 0 && (
            <button class="btn-sm" onclick={() => hostedZoneUpdateHandler(hosted_zone.fqdn)}>
              <span class="fas fa-sync-alt" /> Trigger Update
            </button>
          )}
          {hasAccess(data.account_item.account_id, ['manage_dns']) &&
            ['AccountDeleted', 'NoSuchHostedZone'].indexOf(hosted_zone.status) < 0 && (
              <button class="btn-sm" onclick={() => deleteZoneOrder(hosted_zone_data)}>
                <span class="fas fa-trash-alt" /> Delete Hosted Zone
              </button>
            )}
          {hasAccess(data.account_item.account_id, ['manage_dns']) &&
            ['AccountDeleted', 'NoSuchHostedZone'].indexOf(hosted_zone.status) >= 0 && (
              <button class="btn-sm" onclick={() => deleteHostedZoneFromDB(hosted_zone.fqdn)}>
                <span class="fas fa-trash-alt" /> Delete Hosted Zone From DB
              </button>
            )}
        </div>
      </div>
    );
  };

  const fqdn_id = hosted_zone.fqdn && hosted_zone.fqdn.replaceAll('.', '-');

  const tabs = [
    { id: 'hosted_zone_' + fqdn_id, label: 'Hosted Zone' },
    { id: 'associated_vpcs_' + fqdn_id, label: 'Associated VPCs', disabled: !associatedVPCsEnabled },
    { id: 'name_servers_' + fqdn_id, label: 'Name Server Records', disabled: !nameServersEnabled },
    { id: 'dnssec_' + fqdn_id, label: 'DNSSEC', disabled: !dnssecEnabled },
    { id: 'query_logging_config_' + fqdn_id, label: 'Query Logging Config', disabled: !queryLoggingConfigEnabled },
  ];

  return (
    <DetailsTabLayout tabs={tabs}>
      <TabPane id={'hosted_zone_' + fqdn_id} active name="hosted-zone-container">
        <div class="col-lg-12 col-xl-6">
          <DetailSection title="Hosted Zone Details">
            <InfoTable
              data={hosted_zone}
              rowInfos={[
                { Name: 'Account ID', Value: 'account_id' },
                { Name: 'Name', Value: 'fqdn' },
                { Name: 'Hosted Zone ID', Value: 'zone_id' },
                { Name: 'Zone Type', Value: 'zone_type' },
                { Name: 'Comment', Value: 'comment' },
                { Name: 'Resource Record Set Count', Value: 'zone_details.ResourceRecordSetCount' },
                { Name: 'Status', Value: 'status' },
                { Name: 'Error Details', Value: 'error_details' },
                { Name: 'Status Update', Value: 'status_update' },
              ]}
            />
          </DetailSection>
        </div>
        <div class="col-lg-12 col-xl-6">
          <DetailSection title="Account Details">
            <InfoTable
              data={account_item}
              rowInfos={[
                { Name: 'Account Id', Value: 'account_id' },
                { Name: 'Root Account', Value: 'account_root_email' },
                { Name: 'Friendly Name', Value: 'account_friendly_name' },
                { Name: 'Description', Value: 'description' },
                { Name: 'FPC Status', Value: 'fpc_status' },
                { Name: 'Account Area', Value: 'account_area' },
                { Name: 'Account Type', Value: 'account_type' },
                { Name: 'Account Stage', Value: 'account_stage' },
                { Name: 'AWS Support Plan', Value: 'support_plan' },
                { Name: 'IT Responsible', Value: 'it_responsible' },
                { Name: 'Creation Date', Value: 'creation_date' },
              ]}
            />
          </DetailSection>
        </div>
        <div class="col-lg-12 col-xl-12">{hostedZoneButtonHelper()}</div>
      </TabPane>
      <TabPane id={'associated_vpcs_' + fqdn_id} name="associated-vpcs-container">
        <div class="col-lg-12 col-xl-6">
          <DetailSection title="Associated VPCs">
            <InfoTableWide data={hosted_zone.vpcs || []} colInfos={['VPCRegion', 'VPCId']} rowNumbers={true} />
          </DetailSection>
        </div>
        <div class="col-lg-12 col-xl-12">{hostedZoneButtonHelper()}</div>
      </TabPane>
      <TabPane id={'name_servers_' + fqdn_id} name="name-servers-container">
        <div class="col-lg-12 col-xl-12">
          <DetailSection title="Name Servers">
            <InfoTableWide
              data={hosted_zone.ns_records || []}
              colInfos={['Name', 'ResourceRecords']}
              rowNumbers={true}
            />
          </DetailSection>
        </div>
        <div class="col-lg-12 col-xl-12">{hostedZoneButtonHelper()}</div>
      </TabPane>
      <TabPane id={'dnssec_' + fqdn_id} name="dnssec-container">
        <div class="col-lg-12 col-xl-6">
          <div class="detailsContent">
            <h4>Raw DNSSEC Data</h4>
            {createJsonCodeBlock(hosted_zone.dnssec || '{}')}
          </div>
        </div>
        <div class="col-lg-12 col-xl-12">{hostedZoneButtonHelper()}</div>
      </TabPane>
      <TabPane id={'query_logging_config_' + fqdn_id} name="query-logging-config-container">
        <div class="col-lg-12 col-xl-6">
          <div class="detailsContent">
            <h4>Raw Query Logging Config</h4>
            {createJsonCodeBlock(hosted_zone.query_logging_config || '{}')}
          </div>
        </div>
        <div class="col-lg-12 col-xl-12">{hostedZoneButtonHelper()}</div>
      </TabPane>
    </DetailsTabLayout>
  );
}
